import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as firestoreLimit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/services/logger';
import { syncQueue, type SyncAction } from '@/services/syncQueue';
import { networkMonitor } from '@/services/networkMonitor';

export type MaybeItemTag = 'indoor' | 'quick-stop' | 'high-energy' | 'quiet';
export type MaybeItemSource = 'manual' | 'ai' | 'recommendation';
export type MaybeItemStatus = 'pending' | 'scheduled' | 'archived';

export type MaybeItem = {
  id: string;
  tripId: string;
  title: string;
  description?: string;
  source: MaybeItemSource;
  sourceId?: string;
  tags: MaybeItemTag[];
  createdBy: string;
  createdAt: Date;
  decidedAt?: Date;
  status: MaybeItemStatus;
};

type FirestoreMaybeItem = {
  title: string;
  description?: string | null;
  source: MaybeItemSource;
  sourceId?: string | null;
  tags?: MaybeItemTag[] | null;
  createdBy: string;
  createdAt: Timestamp | Date;
  decidedAt?: Timestamp | Date | null;
  status: MaybeItemStatus;
};

const convertDate = (raw: any): Date | undefined => {
  if (!raw) return undefined;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? undefined : raw;
  if (raw instanceof Timestamp) return raw.toDate();
  if (typeof raw === 'string' || typeof raw === 'number') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? undefined : d;
  }
  if (raw && typeof raw === 'object' && typeof raw.toDate === 'function') {
    try {
      const d = raw.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const toMaybeItem = (tripId: string, id: string, data: FirestoreMaybeItem): MaybeItem => {
  const createdAt = convertDate(data.createdAt) || new Date();
  const decidedAt = convertDate(data.decidedAt);
  return {
    id,
    tripId,
    title: data.title,
    description: data.description || undefined,
    source: data.source,
    sourceId: data.sourceId || undefined,
    tags: (Array.isArray(data.tags) ? data.tags : []).filter(Boolean) as MaybeItemTag[],
    createdBy: data.createdBy,
    createdAt,
    decidedAt,
    status: data.status || 'pending',
  };
};

const colRef = (tripId: string) => collection(db, 'trips', tripId, 'maybeItems');

export const subscribeToMaybeItems = (
  tripId: string,
  callback: (items: MaybeItem[]) => void,
  opts?: { status?: MaybeItemStatus | 'all'; limitCount?: number },
): Unsubscribe => {
  // Avoid requiring composite indexes like (status + createdAt).
  // We'll filter in Firestore (optional) and sort in-memory for stable UX.
  const limitCount = Math.max(1, Math.min(300, opts?.limitCount ?? 120));
  const q = (() => {
    if (!opts?.status || opts.status === 'all') return query(colRef(tripId), firestoreLimit(limitCount));
    return query(colRef(tripId), where('status', '==', opts.status), firestoreLimit(limitCount));
  })();

  let unsub: Unsubscribe | null = null;
  let retryCount = 0;
  const MAX_RETRIES = 2;
  let isClosed = false;

  const start = () => {
    if (isClosed) return;
    
    unsub = onSnapshot(
      q,
      (snap) => {
        if (isClosed) return;
        const out: MaybeItem[] = [];
        snap.forEach((d) => out.push(toMaybeItem(tripId, d.id, d.data() as FirestoreMaybeItem)));
        out.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
        // Reset retry count on successful snapshot
        retryCount = 0;
        callback(out);
      },
      (err) => {
        logger.error('[MaybeItems] subscribe error', err instanceof Error ? err : new Error(String(err)));
        if (isClosed) return;
        
        void (async () => {
          const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
          
          if (isPermissionError(err) && retryCount < MAX_RETRIES) {
            retryCount++;
            logger.info('[MaybeItems] Permission error, attempting membership repair', { tripId, retryCount });
            
            try {
              const result = await syncTripMembership(tripId);
              logger.info('[MaybeItems] Membership repair result', { tripId, success: result?.success });
              
              if (result?.success) {
                if (isClosed) return;
                
                // Unsubscribe from the failed listener
                if (unsub) {
                  try {
                    unsub();
                  } catch {
                    // ignore
                  }
                  unsub = null;
                }
                
                // Small delay before resubscribing to allow Firestore rules to propagate
                await new Promise(resolve => setTimeout(resolve, 500));
                
                if (isClosed) return;
                logger.info('[MaybeItems] Resubscribing after membership repair', { tripId });
                start();
                return;
              }
            } catch (syncErr) {
              logger.error('[MaybeItems] membership sync failed', syncErr instanceof Error ? syncErr : new Error(String(syncErr)));
            }
          }

          // Offline: do not wipe the list so optimistic/last-known items stay visible
          const isOffline = (e: unknown) => {
            const code = (e as { code?: string })?.code;
            return code === 'unavailable' || code === 'failed-precondition';
          };
          if (isOffline(err)) {
            if (__DEV__) {
              logger.info('[MaybeItems] Offline or transient error, not clearing list', { tripId });
            }
            return;
          }

          // Exhausted retries or non-permission error - return empty
          if (!isClosed) {
            callback([]);
          }
        })();
      },
    );
  };

  start();

  return () => {
    isClosed = true;
    if (unsub) {
      try {
        unsub();
      } catch {
        // ignore
      }
      unsub = null;
    }
  };
};

export const createMaybeItem = async (
  tripId: string,
  input: Omit<MaybeItem, 'id' | 'tripId' | 'createdAt' | 'decidedAt' | 'status'> & {
    status?: MaybeItemStatus;
  },
): Promise<string> => {
  const ref = doc(colRef(tripId));
  const payload: Record<string, any> = {
    title: input.title,
    source: input.source,
    createdBy: input.createdBy,
    status: input.status || 'pending',
    createdAt: serverTimestamp(),
  };
  if (input.description !== undefined) payload.description = input.description || null;
  if (input.sourceId !== undefined) payload.sourceId = input.sourceId || null;
  payload.tags = Array.isArray(input.tags) ? input.tags : [];

  try {
    await setDoc(ref, payload);
  } catch (err: any) {
    const code = err?.code as string | undefined;
    if (code === 'unavailable' || code === 'failed-precondition') {
      throw new Error('You appear to be offline. Reconnect and try saving this Maybe item again.');
    }
    // Legacy trips can have collaborators without `members`/`memberIds` — repair and retry once.
    const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
    if (isPermissionError(err)) {
      try {
        const res = await syncTripMembership(tripId);
        if (!res?.success) {
          throw err;
        }
        await setDoc(ref, payload);
      } catch (retryErr: any) {
        logger.error('[MaybeItems] create permission retry failed', retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
        throw retryErr;
      }
    }
    throw err;
  }
  return ref.id;
};

export const createMaybeItemAndFetch = async (
  tripId: string,
  input: Omit<MaybeItem, 'id' | 'tripId' | 'createdAt' | 'decidedAt' | 'status'> & {
    status?: MaybeItemStatus;
  },
): Promise<{ id: string; items: MaybeItem[] }> => {
  const id = await createMaybeItem(tripId, input);
  const status = input.status ?? 'pending';
  const items = await (async () => {
    const list = await getMaybeItems(tripId, status);
    if (list.length > 0) return list;
    const fallback = await getMaybeItem(tripId, id);
    return fallback ? [fallback] : list;
  })();
  return { id, items };
};

export const updateMaybeItem = async (
  tripId: string,
  itemId: string,
  updates: Partial<Pick<MaybeItem, 'title' | 'description' | 'tags' | 'status' | 'decidedAt'>>,
  options?: { bypassOffline?: boolean },
): Promise<void> => {
  if (!options?.bypassOffline && networkMonitor.shouldUseOfflineDataFallback()) {
    await syncQueue.queueAction({
      type: 'update_maybe_item',
      payload: {
        tripId,
        itemId,
        updatesJson: JSON.stringify(updates, (_k, v) => (v instanceof Date ? v.toISOString() : v)),
      },
      tripId,
    });
    return;
  }

  const ref = doc(db, 'trips', tripId, 'maybeItems', itemId);
  const payload: Record<string, any> = {};
  if (updates.title !== undefined) payload.title = updates.title;
  if (updates.description !== undefined) payload.description = updates.description || null;
  if (updates.tags !== undefined) payload.tags = Array.isArray(updates.tags) ? updates.tags : [];
  if (updates.status !== undefined) payload.status = updates.status;
  if (updates.decidedAt !== undefined) payload.decidedAt = updates.decidedAt ? Timestamp.fromDate(updates.decidedAt) : null;
  try {
    await updateDoc(ref, payload);
  } catch (error: any) {
    const code = error?.code as string | undefined;
    if (
      !options?.bypassOffline &&
      (code === 'unavailable' || code === 'failed-precondition' || code === 'deadline-exceeded' || code === 'aborted')
    ) {
      await syncQueue.queueAction({
        type: 'update_maybe_item',
        payload: {
          tripId,
          itemId,
          updatesJson: JSON.stringify(updates, (_k, v) => (v instanceof Date ? v.toISOString() : v)),
        },
        tripId,
      });
      return;
    }
    throw error;
  }
};

export const archiveMaybeItem = async (tripId: string, itemId: string): Promise<void> => {
  if (networkMonitor.shouldUseOfflineDataFallback()) {
    await syncQueue.queueAction({
      type: 'archive_maybe_item',
      payload: {
        tripId,
        itemId,
      },
      tripId,
    });
    return;
  }
  await updateMaybeItem(tripId, itemId, { status: 'archived', decidedAt: new Date() });
};

export const archiveMaybeItemAndFetch = async (
  tripId: string,
  itemId: string,
): Promise<MaybeItem[]> => {
  await archiveMaybeItem(tripId, itemId);
  return getMaybeItems(tripId, 'pending');
};

export const markMaybeItemScheduled = async (tripId: string, itemId: string): Promise<void> => {
  await updateMaybeItem(tripId, itemId, { status: 'scheduled', decidedAt: new Date() });
};

export const markMaybeItemScheduledAndFetch = async (
  tripId: string,
  itemId: string,
): Promise<MaybeItem[]> => {
  await markMaybeItemScheduled(tripId, itemId);
  return getMaybeItems(tripId, 'pending');
};

export const getMaybeItem = async (tripId: string, itemId: string): Promise<MaybeItem | null> => {
  const fetchItem = async () => {
    const ref = doc(db, 'trips', tripId, 'maybeItems', itemId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return toMaybeItem(tripId, snap.id, snap.data() as FirestoreMaybeItem);
  };

  try {
    return await fetchItem();
  } catch (err) {
    try {
      const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
      if (isPermissionError(err)) {
        const res = await syncTripMembership(tripId);
        if (res?.success) {
          return await fetchItem();
        }
      }
    } catch (retryErr) {
      logger.error('[MaybeItems] getMaybeItem retry failed', retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
    }
    logger.error('[MaybeItems] getMaybeItem error', err instanceof Error ? err : new Error(String(err)));
    return null;
  }
};

export const getMaybeItems = async (
  tripId: string,
  status: MaybeItemStatus | 'all' = 'all',
  options?: { limitCount?: number },
): Promise<MaybeItem[]> => {
  const fetchList = async () => {
    const limitCount = Math.max(1, Math.min(300, options?.limitCount ?? 120));
    const q =
      status === 'all'
        ? query(colRef(tripId), firestoreLimit(limitCount))
        : query(colRef(tripId), where('status', '==', status), firestoreLimit(limitCount));
    const snap = await getDocs(q);
    const out: MaybeItem[] = [];
    snap.forEach((d) => out.push(toMaybeItem(tripId, d.id, d.data() as FirestoreMaybeItem)));
    out.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
    return out;
  };

  try {
    return await fetchList();
  } catch (err) {
    try {
      const { isPermissionError, syncTripMembership } = await import('@/services/tripMembership');
      if (isPermissionError(err)) {
        const res = await syncTripMembership(tripId);
        if (res?.success) {
          return await fetchList();
        }
      }
    } catch (retryErr) {
      logger.error('[MaybeItems] getMaybeItems retry failed', retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
    }
    logger.error('[MaybeItems] getMaybeItems error', err instanceof Error ? err : new Error(String(err)));
    return [];
  }
};

syncQueue.registerHandler('update_maybe_item', async (action: SyncAction) => {
  const { tripId, itemId, updatesJson } = action.payload as {
    tripId: string;
    itemId: string;
    updatesJson: string;
  };
  const updates = JSON.parse(updatesJson) as Partial<
    Pick<MaybeItem, 'title' | 'description' | 'tags' | 'status' | 'decidedAt'>
  >;
  if (typeof updates.decidedAt === 'string') {
    const d = new Date(updates.decidedAt);
    if (!Number.isNaN(d.getTime())) {
      updates.decidedAt = d;
    }
  }
  await updateMaybeItem(tripId, itemId, updates, { bypassOffline: true });
});

syncQueue.registerHandler('archive_maybe_item', async (action: SyncAction) => {
  const { tripId, itemId } = action.payload as {
    tripId: string;
    itemId: string;
  };
  await updateMaybeItem(tripId, itemId, { status: 'archived', decidedAt: new Date() }, { bypassOffline: true });
});

